import { LoadingButton } from "@mui/lab";
import { Box, Grid, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { TERMS_AND_CONDITIONS_LINK_YEARLY } from "constants/influencer";
import { getSource, trackEvent } from "utils/tracking";

import styles from "./styles";

interface Props {
  checkoutBundle: () => Promise<void>;
  fetchUserLoading: boolean;
}

const ProgramInfo = ({ checkoutBundle, fetchUserLoading }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { currentOrg } = useContext(OrganizationUserContext);
  const { subscription } = useContext(SubscriptionContext);
  const hasAccessToVa =
    subscription?.isSubscribed &&
    subscription?.subscription?.planName === Plans.VA_YEARLY;

  const clickGo = async () => {
    trackEvent("Virtual Assistant Money Back - Let's Go Button Clicked");
    if (hasAccessToVa) {
      navigate(`/${routes.home}${location.search}`);
      return;
    }
    if (currentOrg?.id) {
      await checkoutBundle();
    } else {
      navigate(`/${routes.vaAnnualSignUp}${location.search}`);
    }
  };

  const trackTermsAndCondition = () => {
    trackEvent("Virtual Assistant Money Back - Terms & Conditions Clicked", {
      source: getSource(),
      clickedFrom: "Virtual Assistant Money Back - Splash Page",
    });
  };

  return (
    <Box sx={{ my: 3, px: { xs: 2, md: 6, lg: 10 } }}>
      <Typography variant="h2" sx={styles.header} component="div">
        <strong>Bento's Virtual Assistant — Money-Back Guarantee</strong>
      </Typography>

      <Typography>
        <em>Keep 100% of your earnings — no rev share, ever.</em>
      </Typography>

      <Typography variant="h6" sx={{ my: 2 }}>
        What you'll get
      </Typography>

      <ul>
        <li>
          ⚡ Onboard in minutes — share your preferences and set your templates.
        </li>
        <li>
          📧 300+ emails sent for you every month — plus all of your follow-ups,
          handled on your behalf.
        </li>
        <li>
          🎯 Change niches or templates anytime — we shift with you as your
          content and business shifts.
        </li>
        <li>
          🏪 Priority access to 10K+ new brands added monthly — always a fresh
          pipeline.
        </li>
        <li>💬 Tier-one customer support — fast help from real humans.</li>
      </ul>

      <Typography variant="h6" sx={{ my: 2 }}>
        Outcomes
      </Typography>

      <ul>
        <li>
          💸 Most creators earn 3–5x ROI in their first 4 months — many see 20x.
        </li>
      </ul>

      <Typography variant="h6" sx={{ my: 2 }}>
        Our Guarantee
      </Typography>
      <ul>
        <li>
          ✅ If you don't earn more than you pay in your first year, we'll
          refund you. No fuss.
        </li>
      </ul>

      <a
        href={TERMS_AND_CONDITIONS_LINK_YEARLY}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackTermsAndCondition}
      >
        Terms & Conditions Apply
      </a>

      <Grid container justifyContent="center" sx={{ my: 3 }}>
        <LoadingButton
          disableElevation
          variant="contained"
          onClick={clickGo}
          loading={fetchUserLoading}
        >
          Get Started
          <Box
            sx={{ ml: 1 }}
            component="i"
            className="fa-regular fa-arrow-right"
          />
        </LoadingButton>
      </Grid>
      <Grid container justifyContent="center">
        <Box sx={styles.limitedTime}>
          <strong>
            <em>Expires Sept 2, 2025</em>
          </strong>
        </Box>
      </Grid>
    </Box>
  );
};

export default ProgramInfo;
